/**
 * @file ButtonPropertiesMultimedia.js
 * @description Properties panel for multimedia container button configuration.
 */

import { useEffect, useState } from 'react';
import './PropertiesMultimedia.css';
import styles from '../assets/AssetLibrary.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { queryPortalPages } from '../../services/portalPageService';
import { Dropdown } from '../helpers/Dropdown';
import SSODropdown from '../shared/asyncDropdowns/SSODropdown';
import { useAuthContext } from '../../store/Contexts';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

function ButtonPropertiesMultimedia({
    item,
    setItem,
    buttonState,
    setButtonState,
    portalId,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [eligiblePortalsList, setEligiblePortalsList] = useState([]);
    const { clientTierMeetsRequirement } = useAuthContext();
    const canUseSSO = clientTierMeetsRequirement(TenantTiers.Bronze);

    const {
        isNoneChecked,
        isWebsiteChecked,
        isPortalChecked,
        isSSOChecked,
        buttonUrl,
        buttonText,
        childPortalId,
        tenantSSOId,
        isInitialized,
    } = buttonState;

    useEffect(() => {
        const portalsArray = [];
        queryPortalPages(true)
            .then(payload => {
                payload.viewModels.forEach(portalPage => {
                    if (portalPage.status === 1 && portalPage.id !== portalId) {
                        const label =
                            portalPage.isChild === false
                                ? `${portalPage.name} (M)`
                                : portalPage.name;
                        portalsArray.push({
                            value: portalPage.id,
                            label,
                        });
                    }
                });
                setEligiblePortalsList(portalsArray);
            })
            .catch(err => {
                console.error('Failed to query portal pages', err);
            });
    }, [portalId]);

    function updateButtonState(updates) {
        setButtonState(prev => ({ ...prev, ...updates }));
    }

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        setItem(prevState => {
            const { multimediaPart } = prevState;
            return {
                ...prevState,
                multimediaPart: {
                    ...multimediaPart,
                    buttonText: isNoneChecked ? '' : buttonText,
                    url: isWebsiteChecked ? buttonUrl : '',
                    childPortalId: isPortalChecked ? childPortalId : null,
                    tenantSSOId: isSSOChecked ? tenantSSOId : null,
                },
            };
        });
    }, [
        isNoneChecked,
        isWebsiteChecked,
        isPortalChecked,
        isSSOChecked,
        buttonUrl,
        buttonText,
        childPortalId,
        tenantSSOId,
        isInitialized,
    ]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateButtonState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            const multimediaPart = item.multimediaPart || {};
            const hasUrl = multimediaPart.url && multimediaPart.url !== '';
            const hasPortal =
                multimediaPart.childPortalId !== null &&
                multimediaPart.childPortalId !== undefined;
            const hasSSO =
                multimediaPart.tenantSSOId !== null &&
                multimediaPart.tenantSSOId !== undefined;

            if (hasUrl) {
                updateButtonState({
                    isNoneChecked: false,
                    isWebsiteChecked: true,
                    isPortalChecked: false,
                    isSSOChecked: false,
                    buttonUrl: multimediaPart.url || '',
                    buttonText: multimediaPart.buttonText || '',
                    childPortalId: null,
                    tenantSSOId: null,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasPortal) {
                updateButtonState({
                    isNoneChecked: false,
                    isWebsiteChecked: false,
                    isPortalChecked: true,
                    isSSOChecked: false,
                    buttonUrl: '',
                    buttonText: multimediaPart.buttonText || '',
                    childPortalId: multimediaPart.childPortalId,
                    tenantSSOId: null,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            if (hasSSO) {
                updateButtonState({
                    isNoneChecked: false,
                    isWebsiteChecked: false,
                    isPortalChecked: false,
                    isSSOChecked: true,
                    buttonUrl: '',
                    buttonText: multimediaPart.buttonText || '',
                    childPortalId: null,
                    tenantSSOId: multimediaPart.tenantSSOId,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            updateButtonState({
                isNoneChecked: true,
                isWebsiteChecked: false,
                isPortalChecked: false,
                isSSOChecked: false,
                buttonUrl: '',
                buttonText: '',
                childPortalId: null,
                tenantSSOId: null,
                isInitialized: true,
            });
            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    function findPortalPageName(portalPageId) {
        const portalPage = eligiblePortalsList.find(
            page => page.value === portalPageId,
        );
        return portalPage ? portalPage.label : `Portal ID: ${portalPageId}`;
    }

    function handlePortalChange(value) {
        updateButtonState({ childPortalId: value });
    }

    function handleSSOChange(ssoId) {
        updateButtonState({ tenantSSOId: ssoId });
    }

    function handleURLChange(event) {
        updateButtonState({ buttonUrl: event.target.value });
    }

    function handleButtonTextChange(event) {
        updateButtonState({ buttonText: event.target.value });
    }

    function handleNoneCheck() {
        updateButtonState({
            isNoneChecked: true,
            isWebsiteChecked: false,
            isPortalChecked: false,
            isSSOChecked: false,
            buttonUrl: '',
            buttonText: '',
            childPortalId: null,
            tenantSSOId: null,
        });
    }

    function handleWebsiteCheck() {
        updateButtonState({
            buttonText: buttonText === '' ? 'View' : buttonText,
            isNoneChecked: false,
            isWebsiteChecked: true,
            isPortalChecked: false,
            isSSOChecked: false,
            childPortalId: null,
            tenantSSOId: null,
        });
    }

    function handlePortalCheck() {
        updateButtonState({
            buttonText: buttonText === '' ? 'View' : buttonText,
            isNoneChecked: false,
            isWebsiteChecked: false,
            isPortalChecked: true,
            isSSOChecked: false,
            buttonUrl: '',
            tenantSSOId: null,
        });
    }

    function handleSSOCheck() {
        updateButtonState({
            buttonText: buttonText === '' ? 'View' : buttonText,
            isNoneChecked: false,
            isWebsiteChecked: false,
            isPortalChecked: false,
            isSSOChecked: true,
            buttonUrl: '',
            childPortalId: null,
        });
    }

    function getSelectedPortalLabel() {
        if (!childPortalId) {
            return 'Select Portal';
        }
        return findPortalPageName(childPortalId);
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_d453fce881fa')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_51967a6b1b8c')}
                </div>
            </div>

            <div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isNoneChecked}
                        onChange={handleNoneCheck}
                    />
                    <label className="form-check-label">{getText('msg_6eef6648406c')}</label>
                </div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isWebsiteChecked}
                        onChange={handleWebsiteCheck}
                    />
                    <label className="form-check-label">{getText('msg_2e8a57cc5c47')}</label>
                </div>
                <div
                    className="form-check"
                    style={{ float: 'left', marginRight: '30px' }}
                >
                    <input
                        className="form-check-input"
                        type="radio"
                        checked={isPortalChecked}
                        onChange={handlePortalCheck}
                    />
                    <label className="form-check-label">{getText('msg_b55223c7bdc7')}</label>
                </div>
                    {canUseSSO && (
                        <div className="form-check" style={{ float: 'left' }}>
                            <input
                                className="form-check-input"
                                type="radio"
                                checked={isSSOChecked}
                                onChange={handleSSOCheck}
                            />
                            <label className="form-check-label">{getText('msg_5fa55a6001cc')}</label>
                        </div>
                    )}
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isWebsiteChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_08738f6eef34')}
                </div>
                <input
                    onChange={handleURLChange}
                    placeholder={getText('msg_324f0f9ff976')}
                    value={buttonUrl}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                    className="form-control"
                    type="text"
                    aria-label={getText('msg_eca7a939ae6d')}
                    disabled={areInputsDisabled}
                />
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isPortalChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_b55223c7bdc7')}
                </div>
                <Dropdown
                    allowSearch
                    defaultValue={getSelectedPortalLabel()}
                    options={eligiblePortalsList}
                    onChange={handlePortalChange}
                />
            </div>

            <div
                style={{
                    marginTop: '64px',
                    display:
                        areInputsDisabled || !isSSOChecked ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_5fa55a6001cc')}
                </div>
                <SSODropdown
                    inlineLabel={false}
                    label=""
                    selectedSSO={tenantSSOId ?? 0}
                    onSSOChange={handleSSOChange}
                    client
                    displayRelative={true}
                />
            </div>

            <div
                style={{
                    marginTop: '16px',
                    display:
                        areInputsDisabled || isNoneChecked ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '8px',
                    }}
                >
                    {getText('msg_a88d151f8d79')}
                </div>
                <input
                    onChange={handleButtonTextChange}
                    placeholder={getText('msg_9e76c024a841')}
                    value={buttonText}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                    className="form-control"
                    type="text"
                    aria-label={getText('msg_842d4631a3d1')}
                    disabled={areInputsDisabled}
                />
            </div>
        </>
    );
}

export default ButtonPropertiesMultimedia;
